// V2 Home — the prototype's composer hero. A prompt + an "Add a brief" upload
// panel + a "Paper & ink" theme panel + starters, then recent decks. Hands the
// brief (and any uploaded documents) to the Studio. Light DOM; styled by
// home.css + pages-v2.css. Mirrors prototype home.jsx Home().

import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../api/client';
import { navigate } from '../lib/router-state';
import { mountThumbs } from '../lib/thumb';
import { isEmptyDraft } from '../lib/deck-util';
import { setPageTitle } from '../lib/page-title';
import type { Deck } from '../api/types';
import type { SlideDeck } from '../lib/slide-model';

const I = {
  paperclip: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>`,
  file: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>`,
  x: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>`,
  download: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>`,
  check: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>`,
  present: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="13" rx="2"/><path d="M12 17v4M8 21h8"/></svg>`,
  target: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4"/></svg>`,
  chart: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19V5M4 19h16M8 16l4-5 3 3 4-6"/></svg>`,
  bulb: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-4 10c1 1 1 2 1 3h6c0-1 0-2 1-3a6 6 0 0 0-4-10z"/></svg>`,
};

const STARTERS = [
  { icon: I.present, label: 'Board or exec update' },
  { icon: I.target, label: 'Customer pitch' },
  { icon: I.chart, label: 'Results readout' },
  { icon: I.bulb, label: 'Product brief' },
];
const THEME_OPTS = [
  { id: 'workplace-paper', name: 'Paper', sw: '#F4F2EE' },
  { id: 'workplace-slate', name: 'Slate', sw: '#15171b' },
  { id: 'warm', name: 'Warm', sw: '#F2EFE6' },
];
const INKS = [
  { id: 'emerald', name: 'RDx green', hex: '#00A76F' },
  { id: 'ink', name: 'Workplace ink', hex: '#1E2A3A' },
  { id: 'sky', name: 'Slate blue', hex: '#2C5470' },
  { id: 'amber', name: 'Clay', hex: '#B87333' },
  { id: 'rose', name: 'Brick', hex: '#A03020' },
];

interface Brief { name: string; mime: string; b64: string }

@customElement('home-page')
export class HomePage extends LitElement {
  @state() private prompt = '';
  @state() private panel: 'brief' | 'theme' | null = null;
  @state() private briefs: Brief[] = [];
  @state() private recent: Deck[] = [];
  @state() private loaded = false;
  @state() private theme = document.documentElement.getAttribute('data-theme') || 'workplace-slate';
  @state() private ink = (getComputedStyle(document.documentElement).getPropertyValue('--brand-color').trim() || '#00A76F');

  protected createRenderRoot(): HTMLElement { return this; }

  connectedCallback(): void {
    super.connectedCallback();
    setPageTitle('Home', 'Craft board-grade presentations and documents, grounded in your own knowledge. By RDx.');
    void this.load();
  }

  private async load(): Promise<void> {
    try {
      // Hide empty drafts (a deck created on the first chat message but with no
      // slides yet) so abandoned conversations don't clutter the recents.
      this.recent = (await api.listDecks()).filter((d) => !isEmptyDraft(d)).slice(0, 6);
    } catch { /* empty ok */ } finally { this.loaded = true; }
  }

  protected updated(): void {
    mountThumbs(this, (id) => {
      const m = this.recent.find((d) => d.id === id)?.model as unknown as SlideDeck | undefined;
      return m && m.schemaVersion === 2 ? m.slides[0] : undefined;
    });
  }

  private setTheme(id: string): void {
    this.theme = id;
    document.documentElement.setAttribute('data-theme', id);
    try { localStorage.setItem('rdx-decks-theme', id); } catch { /* ignore */ }
  }
  private setInk(hex: string): void {
    this.ink = hex;
    document.documentElement.style.setProperty('--brand-color', hex);
    try { localStorage.setItem('rdx-decks-ink', hex); } catch { /* ignore */ }
  }

  private async onPickFiles(e: Event): Promise<void> {
    const input = e.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    for (const f of files) {
      const b64 = await fileToBase64(f);
      this.briefs = [...this.briefs, { name: f.name, mime: f.type || 'application/octet-stream', b64 }];
    }
    input.value = '';
  }
  private removeBrief(name: string): void { this.briefs = this.briefs.filter((b) => b.name !== name); }

  private start(seed?: string): void {
    const q = (seed ?? this.prompt).trim();
    // Carry the brief + any uploaded files to the Studio via sessionStorage —
    // NOT the query string, which has length limits and leaks the brief into the
    // URL/history. The Studio reads + clears these on mount.
    try {
      sessionStorage.setItem('rdx-decks-briefs', JSON.stringify(this.briefs));
      if (q) sessionStorage.setItem('rdx-decks-seed', q);
      else sessionStorage.removeItem('rdx-decks-seed');
    } catch { /* ignore */ }
    navigate('/studio');
  }

  private onKey(e: KeyboardEvent): void {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); this.start(); }
  }

  render(): TemplateResult {
    return html`
      <div class="home">
        <section class="hero">
          <div class="hero-eyebrow">✦ Start with a prompt</div>
          <h1 class="hero-title">What should we build?</h1>
          <p class="hero-sub">Describe the deck in a sentence. The co-pilot asks a couple of quick questions, then drafts it with you — slide by slide.</p>

          <div class="composer-wrap">
            <div class="composer">
              <textarea class="composer-input" rows="3" placeholder="e.g. A board update on where our workplace platform stands at the end of Q3…"
                .value=${this.prompt}
                @input=${(e: Event) => { this.prompt = (e.target as HTMLTextAreaElement).value; }}
                @keydown=${(e: KeyboardEvent) => this.onKey(e)}></textarea>
              ${this.briefs.length ? html`<div class="composer-briefs">
                ${this.briefs.map((b) => html`<span class="ctx-pill">${I.file}${b.name}
                  <button aria-label="Remove" @click=${() => this.removeBrief(b.name)}>${I.x}</button></span>`)}
              </div>` : nothing}
              <div class="composer-bar">
                <div class="composer-tools">
                  <button class="btn btn--ghost btn--sm ${this.panel === 'brief' ? 'is-on' : ''}" @click=${() => { this.panel = this.panel === 'brief' ? null : 'brief'; }}>
                    ${I.paperclip} Add a brief${this.briefs.length ? html`<span class="tool-count">${this.briefs.length}</span>` : nothing}
                  </button>
                  <button class="btn btn--ghost btn--sm ${this.panel === 'theme' ? 'is-on' : ''}" @click=${() => { this.panel = this.panel === 'theme' ? null : 'theme'; }}>
                    <span class="ink-dot" style="background:${this.ink}"></span> Theme
                  </button>
                  <div class="composer-hint faint"><kbd>⌘</kbd><kbd>⏎</kbd> to start</div>
                </div>
                <button class="btn btn--ai" @click=${() => this.start()}>✦ Draft with AI</button>
              </div>
            </div>

            ${this.panel === 'brief' ? html`
              <div class="cpanel">
                <div class="cpanel-head">${I.paperclip} Ground the deck in a brief
                  <span class="spacer"></span>
                  <button class="icon-btn" aria-label="Close" @click=${() => { this.panel = null; }}>${I.x}</button></div>
                <label class="dropzone">
                  <input type="file" hidden multiple accept=".txt,.md,.csv,.json,.html,text/*,application/pdf" @change=${(e: Event) => this.onPickFiles(e)}>
                  ${I.download}<div><b>Upload a file</b> — I'll cite it as I draft.</div>
                </label>
                ${this.briefs.length ? html`<div class="brief-list">
                  ${this.briefs.map((b) => html`<div class="brief-row on">
                    <span class="brief-ico">${I.file}</span><span class="brief-name">${b.name}</span><span class="brief-check">${I.check}</span></div>`)}
                </div>` : nothing}
              </div>` : nothing}

            ${this.panel === 'theme' ? html`
              <div class="cpanel">
                <div class="cpanel-head">Paper &amp; ink<span class="spacer"></span>
                  <button class="icon-btn" aria-label="Close" @click=${() => { this.panel = null; }}>${I.x}</button></div>
                <div class="tt-tag">Paper</div>
                <div class="paper-row">${THEME_OPTS.map((p) => html`
                  <button class="paper-opt ${this.theme === p.id ? 'on' : ''}" @click=${() => this.setTheme(p.id)}>
                    <span class="paper-sw" style="background:${p.sw}"></span>${p.name}</button>`)}</div>
                <div class="tt-tag" style="margin-top:14px">Ink — the brand colour</div>
                <div class="ink-row">${INKS.map((k) => html`
                  <button class="ink-opt ${this.ink.toLowerCase() === k.hex.toLowerCase() ? 'on' : ''}" title=${k.name} @click=${() => this.setInk(k.hex)}>
                    <span class="ink-sw" style="background:${k.hex}"></span>${k.name}</button>`)}</div>
              </div>` : nothing}

            <div class="starters">${STARTERS.map((s) => html`
              <button class="starter" @click=${() => this.start(s.label)}><span class="starter-ico">${s.icon}</span>${s.label}</button>`)}</div>
          </div>
        </section>

        ${this.recent.length ? html`
          <section class="recent">
            <div class="recent-head"><h2 class="recent-title">Recent decks</h2>
              <button class="btn btn--ghost btn--sm" @click=${() => navigate('/library')}>All decks</button></div>
            <div class="recent-grid deck-grid">
              ${this.recent.map((d) => html`
                <button class="deck-card" @click=${() => navigate(`/studio?deck=${encodeURIComponent(d.id)}`)}>
                  <div class="deck-thumb" data-thumb=${d.id}></div>
                  <div class="deck-meta"><div class="deck-meta-top"><h3 class="deck-title">${d.title}</h3></div>
                    <div class="deck-sub"><span class="faint">${d.status === 'draft' ? 'Draft' : 'Saved'}</span></div></div>
                </button>`)}
            </div>
          </section>`
          : this.loaded ? html`<section class="recent"><div class="empty">No decks yet — start one above.</div></section>` : nothing}
      </div>`;
  }
}

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

declare global {
  interface HTMLElementTagNameMap { 'home-page': HomePage }
}
